import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Something went wrong.');
      }

      toast.success(data.message || 'Thank you for your inquiry! We will contact you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: ''
      });
    } catch (error) {
      toast.error(error.message || 'Unable to send your message right now. Please try again.');
    }
  };

  return (
    <div>
      <section 
        className="relative py-24 bg-slate-900 text-white overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1556761175-4b46a572b786?q=80&w=2074&auto=format&fit=crop)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-slate-900/90"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 lg:px-12 text-center">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4" data-testid="contact-heading">
            Get in Touch
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Ready to optimize your industrial operations? Contact us today
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="space-y-8">
              <div className="bg-white border-2 border-slate-200 rounded-sm p-8 hover:border-[#1E56A0] transition-all">
                <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center">
                  <MapPin className="w-6 h-6 text-[#1E56A0] mr-3" />
                  Corporate Office
                </h3>
                <address className="text-slate-600 not-italic leading-relaxed text-base" data-testid="contact-address-corporate">
                  Plot No. 497, 3rd Floor<br />
                  Bharthal, Sector-26<br />
                  Dwarka<br />
                  New Delhi – 110077<br />
                  India
                </address>
              </div>

              <div className="bg-white border-2 border-slate-200 rounded-sm p-8 hover:border-[#FF7F2A] transition-all">
                <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center">
                  <MapPin className="w-6 h-6 text-[#FF7F2A] mr-3" />
                  Workshop
                </h3>
                <address className="text-slate-600 not-italic leading-relaxed text-base" data-testid="contact-address-workshop">
                  WZ-502, Sadh Nagar<br />
                  Street 17-B<br />
                  Palam<br />
                  New Delhi – 110045<br />
                  India
                </address>
              </div>

              <div className="bg-gradient-to-br from-[#1E56A0] to-[#164e8a] text-white rounded-sm p-8 shadow-lg">
                <h3 className="text-xl font-semibold mb-6">Contact Details</h3>
                <div className="space-y-6">
                  <div>
                    <h4 className="font-medium text-sm text-slate-200 mb-2">Contact Person</h4>
                    <p className="text-lg font-semibold">Mr. Shantanu</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-sm text-slate-200 mb-2">Phone</h4>
                    <a href="tel:+919953804787" className="text-lg font-semibold hover:text-[#FFB84D] transition-colors" data-testid="contact-phone">
                      +91 9953804787
                    </a>
                  </div>
                  <div>
                    <h4 className="font-medium text-sm text-slate-200 mb-2">Email</h4>
                    <a href="mailto:Support@ushaautomationiiot.in" className="text-lg font-semibold hover:text-[#FFB84D] transition-colors break-all" data-testid="contact-email">
                      Support@ushaautomationiiot.in
                    </a>
                  </div>
                </div>
                <div className="mt-8 pt-6 border-t border-white/20">
                  <h4 className="font-semibold mb-4">Business Hours</h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-200">Monday - Friday</span>
                      <span className="font-medium">9:00 AM - 6:00 PM</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-200">Saturday</span>
                      <span className="font-medium">9:00 AM - 2:00 PM</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-200">Sunday</span>
                      <span className="font-medium">Closed</span>
                    </div>
                    <p className="text-[#FFB84D] font-semibold mt-4 flex items-center">
                      <span className="w-2 h-2 bg-[#FFB84D] rounded-full mr-2 animate-pulse"></span>
                      24/7 Emergency Support Available
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="bg-slate-50 border-2 border-slate-200 rounded-sm p-8 shadow-lg">
                <h2 className="text-2xl font-semibold text-slate-900 mb-8">Send Us a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-2">
                        Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border-2 border-slate-300 rounded-sm focus:ring-2 focus:ring-[#1E56A0] focus:border-[#1E56A0] transition-all"
                        data-testid="contact-input-name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border-2 border-slate-300 rounded-sm focus:ring-2 focus:ring-[#1E56A0] focus:border-[#1E56A0] transition-all"
                        data-testid="contact-input-email"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-semibold text-slate-700 mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border-2 border-slate-300 rounded-sm focus:ring-2 focus:ring-[#1E56A0] focus:border-[#1E56A0] transition-all"
                        data-testid="contact-input-phone"
                      />
                    </div>
                    <div>
                      <label htmlFor="company" className="block text-sm font-semibold text-slate-700 mb-2">
                        Company
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border-2 border-slate-300 rounded-sm focus:ring-2 focus:ring-[#1E56A0] focus:border-[#1E56A0] transition-all"
                        data-testid="contact-input-company"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-slate-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows="6"
                      className="w-full px-4 py-3 border-2 border-slate-300 rounded-sm focus:ring-2 focus:ring-[#1E56A0] focus:border-[#1E56A0] transition-all resize-none"
                      data-testid="contact-input-message"
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-[#FF7F2A] text-white hover:bg-[#e66e1f] rounded-sm px-6 py-4 font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center"
                    data-testid="contact-submit-button"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    Send Message
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
